import { type FaceDetectionResult, type PhotoFaceData } from './PhotoBank.types'
import { FaceDetectionResultSchema } from './PhotoBank.schemas'
import axios from 'axios'
import axiosBetterStacktrace from 'axios-better-stacktrace'
import { z } from 'zod'
import { analytics, EVENT_NAMES } from 'services/Analytics/Analytics'
import { amplitude } from 'services/Analytics/platforms/amplitude'
import { extractErrorMessage } from 'utils/ApiUtils'
import { runAsyncWithRetries } from 'utils/promises'

const axiosClient = axios.create()

// Without this change axios truncates the stack trace of the error
// see: https://github.com/axios/axios/issues/2387
axiosBetterStacktrace(axiosClient)

export function tracingHeaders() {
  return {
    'X-Amplitude-Device-Id': amplitude.getDeviceId() ?? '',
    'X-Amplitude-Session-Id': String(amplitude.getSessionId() ?? ''),
  }
}

export function markSmallFaces(
  faces: PhotoFaceData[],
  ratio = 9
): { faces: PhotoFaceData[]; smallFacesFiltered: number } {
  if (faces.length <= 1) return { faces, smallFacesFiltered: 0 }
  const maxArea = Math.max(...faces.map((f) => f.width * f.height))
  const marked = faces.map((f) =>
    f.width * f.height < maxArea / ratio
      ? { ...f, belowCropThreshold: true }
      : f
  )
  const smallFacesFiltered = marked.filter((f) => f.belowCropThreshold).length
  return { faces: marked, smallFacesFiltered }
}

function parseFaceDetectionResults(
  responseData: unknown,
  shouldFilterSmallFaces: boolean
) {
  const results = z.array(FaceDetectionResultSchema).parse(responseData)
  if (!shouldFilterSmallFaces) return results
  return results.map((result) => ({
    ...result,
    ...markSmallFaces(result.faces),
  }))
}

export async function detectFacesFromFiles(
  files: File[],
  includeHappyData: boolean = false,
  shouldFilterSmallFaces: boolean = false
): Promise<FaceDetectionResult[]> {
  try {
    const formData = new FormData()
    files.forEach((file) => formData.append('files', file))
    const response = await runAsyncWithRetries({
      op: () =>
        axios.post(
          `/photo-api-v2/v3/faces?includeHappyData=${includeHappyData}`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              ...tracingHeaders(),
            },
          }
        ),
      opName: 'detectFacesFromFiles',
      numRetries: 3,
    })

    return parseFaceDetectionResults(response.data, shouldFilterSmallFaces)
  } catch (error) {
    analytics.track(EVENT_NAMES.PHOTO_FACE_DETECTION_ONLINE_FAILED, {
      Source: 'file',
      'Total Count': files.length,
      Error: extractErrorMessage(error),
      'Request Id': axios.isAxiosError(error)
        ? error.response?.headers?.['x-request-id']
        : undefined,
      'File Names': files.map((f) => f.name).join(', '),
      'File Sizes (MB)': files
        .map((f) => (f.size / (1024 * 1024)).toFixed(2))
        .join(', '),
      'File Types': files.map((f) => f.type).join(', '),
    })
    return []
  }
}

export async function detectFacesFromUrl(
  urls: string[],
  includeHappyData: boolean = false,
  shouldFilterSmallFaces: boolean = false
): Promise<FaceDetectionResult[]> {
  try {
    const response = await runAsyncWithRetries({
      op: () =>
        axios.post(
          `/photo-api-v2/v3/faces-url?includeHappyData=${includeHappyData}`,
          { urls },
          { headers: tracingHeaders() }
        ),
      opName: 'detectFacesFromUrl',
      numRetries: 3,
    })

    return parseFaceDetectionResults(response.data, shouldFilterSmallFaces)
  } catch (error) {
    analytics.track(EVENT_NAMES.PHOTO_FACE_DETECTION_ONLINE_FAILED, {
      Source: 'url',
      'Total Count': urls.length,
      Error: extractErrorMessage(error),
      'Request Id': axios.isAxiosError(error)
        ? error.response?.headers?.['x-request-id']
        : undefined,
      // A private photo is sent as a presigned URL, whose query string is a credential
      URLs: urls.map((url) => url.split('?')[0]).join(', '),
    })
    return []
  }
}
